import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { ArrowLeft, Plus, Minus, X, Trash2, Clock, Pencil } from 'lucide-react';
import { format } from 'date-fns';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '';
const fmt12 = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ap = h >= 12 ? 'PM' : 'AM';
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${String(m).padStart(2, '0')} ${ap}`;
};

// Split a 24h "HH:MM" string into 12h hour/minute/AM-PM parts
const split12 = (t) => {
  if (!t) return { hour: '', minute: '', ap: 'AM' };
  const [h, m] = t.split(':').map(Number);
  return { hour: String(((h + 11) % 12) + 1), minute: String(m).padStart(2, '0'), ap: h >= 12 ? 'PM' : 'AM' };
};

// Combine 12h hour/minute/AM-PM parts into a 24h "HH:MM" string
const join24 = (hour, minute, ap) => {
  if (hour === '' || minute === '') return '';
  let h = parseInt(hour, 10) % 12;
  if (ap === 'PM') h += 12;
  return `${String(h).padStart(2, '0')}:${String(parseInt(minute, 10)).padStart(2, '0')}`;
};

function ManualTimeInput({ value, onChange }) {
  const { hour, minute, ap } = split12(value);

  const setHour = (v) => {
    if (v !== '' && (Number(v) < 1 || Number(v) > 12)) return;
    onChange(join24(v, minute === '' ? '00' : minute, ap));
  };
  const setMinute = (v) => {
    if (v !== '' && (Number(v) < 0 || Number(v) > 59)) return;
    onChange(join24(hour === '' ? '12' : hour, v, ap));
  };
  const setAp = (v) => {
    onChange(join24(hour === '' ? '12' : hour, minute === '' ? '00' : minute, v));
  };

  return (
    <div className="manual-time-input">
      <input type="number" className="form-input" min="1" max="12" placeholder="HH" value={hour}
        onChange={(e) => setHour(e.target.value)} />
      <span>:</span>
      <input type="number" className="form-input" min="0" max="59" placeholder="MM" value={minute}
        onChange={(e) => setMinute(e.target.value)} />
      <select className="form-select" value={ap} onChange={(e) => setAp(e.target.value)}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}

export default function ChecklistDetailPage() {
  const { staffId } = useParams();
  const navigate = useNavigate();

  const [staff, setStaff] = useState(null);
  const [date] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [day, setDay] = useState({ checklist: null, slots: [] });
  const [loading, setLoading] = useState(true);

  // Add-slot modal state
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [checklists, setChecklists] = useState([]);
  const [builderChecklistId, setBuilderChecklistId] = useState('');
  const [poolItems, setPoolItems] = useState([]);
  const [existingSlots, setExistingSlots] = useState([]); // raw slots from current assignment (with taskId)
  const [newSlot, setNewSlot] = useState({ from: '', to: '', label: '', selected: [] });
  const [editingIndex, setEditingIndex] = useState(null); // index of slot being edited, or null when adding
  const collapsedKey = `checklistCollapsed_${staffId}`;
  const [collapsedSlots, setCollapsedSlots] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(collapsedKey)) || {};
    } catch {
      return {};
    }
  }); // { [slotIndex]: true } when collapsed

  useEffect(() => {
    api.get(`/staff/${staffId}`).then((r) => setStaff(r.data)).catch(() => setStaff(null));
  }, [staffId]);

  const loadDay = useCallback(() => {
    setLoading(true);
    api.get(`/staff-checklists/${staffId}/day?date=${date}`)
      .then((r) => setDay(r.data || { checklist: null, slots: [] }))
      .catch(() => setDay({ checklist: null, slots: [] }))
      .finally(() => setLoading(false));
  }, [staffId, date]);

  useEffect(() => { loadDay(); }, [loadDay]);

  // ---- Add Slot ----
  const openAddSlot = async () => {
    try {
      const [clRes, asRes] = await Promise.all([
        api.get('/checklists'),
        api.get(`/staff-checklists/${staffId}`),
      ]);
      setChecklists(clRes.data || []);
      const existing = asRes.data;
      if (existing && existing.checklist?._id) {
        const cid = existing.checklist._id;
        setBuilderChecklistId(cid);
        setExistingSlots(existing.slots || []);
        const pool = await api.get(`/checklists/${cid}`);
        const items = pool.data.items || [];
        setPoolItems(items);
        setNewSlot({ from: '', to: '', label: '', selected: items.map((it) => String(it._id)) });
      } else {
        setBuilderChecklistId('');
        setExistingSlots([]);
        setPoolItems([]);
        setNewSlot({ from: '', to: '', label: '', selected: [] });
      }
      setEditingIndex(null);
      setShowSlotModal(true);
    } catch (err) {
      toast.error('Could not load checklists');
    }
  };

  // ---- Edit Slot ----
  const openEditSlot = async (idx) => {
    try {
      const [clRes, asRes] = await Promise.all([
        api.get('/checklists'),
        api.get(`/staff-checklists/${staffId}`),
      ]);
      setChecklists(clRes.data || []);
      const existing = asRes.data;
      if (!existing || !existing.checklist?._id) return;

      const cid = existing.checklist._id;
      const allSlots = existing.slots || [];
      const target = allSlots[idx];
      if (!target) return;

      setBuilderChecklistId(cid);
      setExistingSlots(allSlots.filter((_, i) => i !== idx));

      const pool = await api.get(`/checklists/${cid}`);
      setPoolItems(pool.data.items || []);

      setNewSlot({
        from: target.from || '',
        to: target.to || '',
        label: target.label || '',
        selected: (target.items || []).map((it) => String(it.taskId)),
      });
      setEditingIndex(idx);
      setShowSlotModal(true);
    } catch (err) {
      toast.error('Could not load slot');
    }
  };

  // ---- Delete Slot ----
  const deleteSlot = async (idx) => {
    if (!window.confirm('Remove this slot?')) return;
    try {
      const asRes = await api.get(`/staff-checklists/${staffId}`);
      const existing = asRes.data;
      if (!existing) return;
      const remainingSlots = (existing.slots || [])
        .filter((_, i) => i !== idx)
        .map((s) => ({
          from: s.from || '',
          to: s.to || '',
          label: s.label || '',
          items: (s.items || []).map((it) => ({ taskId: it.taskId, title: it.title, description: it.description || '' })),
        }));
      await api.put(`/staff-checklists/${staffId}`, { checklist: existing.checklist?._id, slots: remainingSlots });
      toast.success('Slot removed');
      loadDay();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    }
  };

  const pickChecklist = async (cid) => {
    setBuilderChecklistId(cid);
    if (!cid) { setPoolItems([]); setNewSlot((s) => ({ ...s, selected: [] })); return; }
    try {
      const r = await api.get(`/checklists/${cid}`);
      const items = r.data.items || [];
      setPoolItems(items);
      setNewSlot((s) => ({ ...s, selected: items.map((it) => String(it._id)) }));
    } catch { setPoolItems([]); }
  };

  const updateNewSlot = (patch) => setNewSlot((s) => ({ ...s, ...patch }));
  const toggleNewSlotItem = (taskId) => setNewSlot((s) => {
    const has = s.selected.includes(taskId);
    return { ...s, selected: has ? s.selected.filter((t) => t !== taskId) : [...s.selected, taskId] };
  });

  const saveNewSlot = async () => {
    if (!builderChecklistId) { toast.error('Pick a checklist'); return; }
    if (newSlot.selected.length === 0) { toast.error('Select at least one task'); return; }

    const existingPayload = existingSlots.map((s) => ({
      from: s.from || '',
      to: s.to || '',
      label: s.label || '',
      items: (s.items || []).map((it) => ({ taskId: it.taskId, title: it.title, description: it.description || '' })),
    }));

    const newPayload = {
      from: newSlot.from,
      to: newSlot.to,
      label: newSlot.label,
      items: newSlot.selected.map((tid) => {
        const it = poolItems.find((p) => String(p._id) === String(tid));
        return { taskId: tid, title: it?.title || '', description: it?.description || '' };
      }),
    };

    const merged = [...existingPayload];
    if (editingIndex !== null) {
      merged.splice(editingIndex, 0, newPayload);
    } else {
      merged.push(newPayload);
    }

    try {
      await api.put(`/staff-checklists/${staffId}`, { checklist: builderChecklistId, slots: merged });
      toast.success(editingIndex !== null ? 'Slot updated' : 'Slot added');
      setShowSlotModal(false);
      loadDay();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    }
  };

  const toggleSlotCollapsed = (slotId) => setCollapsedSlots((c) => {
    const next = { ...c, [slotId]: !c[slotId] };
    localStorage.setItem(collapsedKey, JSON.stringify(next));
    return next;
  });

  if (!staff) return <div className="page"><div className="empty-state">Loading...</div></div>;

  const totalItems = day.slots.reduce((n, s) => n + s.items.length, 0);
  const doneItems = day.slots.reduce((n, s) => n + s.items.filter((i) => i.completed).length, 0);
  const slotNumber = editingIndex !== null ? editingIndex + 1 : existingSlots.length + 1;

  return (
    <div className="page">
      <div className="page-header">
        <div className="staff-header-card" style={{ border: 'none', padding: 0, margin: 0, background: 'transparent' }}>
          <button className="back-arrow-btn" onClick={() => navigate('/checklist-status')} title="Back">
            <ArrowLeft size={22} />
          </button>
          {staff.photo && <img src={`${API_BASE}/uploads/${staff.photo}`} alt="" className="staff-photo-sm" />}
          <div>
            <h2 style={{ fontSize: 20 }}>{staff.name}</h2>
            <p className="text-muted">{staff.staffId} · {staff.outlet?.name}{day.checklist ? ` · ${day.checklist.name}` : ''}</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={openAddSlot}>
            <Plus size={16} /> Add Slot
          </button>
        </div>
      </div>

      {day.slots.length > 0 && (
        <div className="text-muted" style={{ marginBottom: 12 }}>{doneItems} of {totalItems} completed</div>
      )}

      {loading && <div className="empty-state">Loading...</div>}
      {!loading && day.slots.length === 0 && (
        <div className="empty-state">No checklist assigned. Click “Add Slot” to build {staff.name}’s schedule.</div>
      )}

      {!loading && day.slots.map((s, idx) => {
        const collapsed = !!collapsedSlots[idx];
        return (
        <div key={s._id} className="slot-block">
          <div className="slot-head">
            <button className="btn btn-sm btn-outline btn-icon" onClick={() => toggleSlotCollapsed(idx)} title={collapsed ? 'Expand' : 'Collapse'}>
              {collapsed ? <Plus size={13} /> : <Minus size={13} />}
            </button>
            <Clock size={14} />
            <span>
              {s.items.length} task{s.items.length === 1 ? '' : 's'} ·{' '}
              {s.from ? fmt12(s.from) : ''}{s.from && s.to ? ' – ' : ''}{s.to ? fmt12(s.to) : ''}
              {(s.from || s.to) && s.label ? ' · ' : ''}{s.label}
            </span>
            <span className="slot-head-actions">
              <button className="btn btn-sm btn-outline btn-icon" onClick={() => openEditSlot(idx)} title="Edit slot"><Pencil size={13} /></button>
              <button className="btn btn-sm btn-outline btn-icon" onClick={() => deleteSlot(idx)} title="Delete slot"><Trash2 size={13} /></button>
            </span>
          </div>
          {!collapsed && <div className="checklist-list">
            {s.items.map((it) => (
              <div key={it.slotItem} className={`checklist-row ${it.completed ? 'done' : ''}`}>
                <label className="checklist-check" style={{ cursor: 'default' }}>
                  <input type="checkbox" checked={it.completed} readOnly disabled />
                  <span className="checklist-text">
                    <span className="checklist-title">{it.title}</span>
                    {it.description && <span className="checklist-desc">{it.description}</span>}
                  </span>
                </label>
                {it.completed && it.completedAt && <span className="checklist-time">✓ {fmtTime(it.completedAt)}</span>}
              </div>
            ))}
          </div>}
        </div>
        );
      })}

      {/* Add Slot modal */}
      {showSlotModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-row">
              <h3>Slot {slotNumber}</h3>
              <button className="btn btn-sm btn-outline btn-icon" onClick={() => setShowSlotModal(false)}><X size={16} /></button>
            </div>

            <div className="form-group">
              <label>Checklist *</label>
              <select value={builderChecklistId} onChange={(e) => pickChecklist(e.target.value)}>
                <option value="">Select a checklist…</option>
                {checklists.map((c) => <option key={c._id} value={c._id}>{c.name} ({c.itemCount || 0} tasks)</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>Time Slot</label>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <ManualTimeInput value={newSlot.from} onChange={(v) => updateNewSlot({ from: v })} />
                <span className="text-muted">to</span>
                <ManualTimeInput value={newSlot.to} onChange={(v) => updateNewSlot({ to: v })} />
              </div>
            </div>


            <div className="form-group">
              <label>Tasks</label>
              {builderChecklistId && poolItems.length === 0 && (
                <div className="empty-state">This checklist has no tasks. Add tasks under Checklist first.</div>
              )}
              {!builderChecklistId && (
                <div className="empty-state">Select a checklist to see its tasks.</div>
              )}
              {poolItems.length > 0 && (
                <div className="slot-pick-list">
                  {poolItems.map((it) => (
                    <label key={it._id} className="slot-pick">
                      <input type="checkbox"
                        checked={newSlot.selected.includes(String(it._id))}
                        onChange={() => toggleNewSlotItem(String(it._id))} />
                      <span>{it.title}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <span className="text-muted" style={{ marginRight: 'auto', fontSize: 13 }}>{newSlot.selected.length} selected</span>
              <button type="button" className="btn btn-outline" onClick={() => setShowSlotModal(false)}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={saveNewSlot}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
